import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

/**
 * Legal Workflow Status Definitions
 * Status 1 = Draft/Created (not paid)
 * Status 2 = Saved/Pending (awaiting payment or validation)
 * Status 3 = Published (paid and completed)
 */
const WORKFLOW_STATUSES = {
  DRAFT: { value: 1, label: 'Brouillons', color: 'gray', bgColor: 'bg-gray-100', textColor: 'text-gray-700', borderColor: 'border-gray-300' },
  PENDING: { value: 2, label: 'En attente', color: 'amber', bgColor: 'bg-amber-50', textColor: 'text-amber-700', borderColor: 'border-amber-300' },
  PUBLISHED: { value: 3, label: 'Publiées', color: 'green', bgColor: 'bg-green-50', textColor: 'text-green-700', borderColor: 'border-green-300' },
};

/**
 * Single workflow status card
 */
function WorkflowStatusCard({ 
  label, 
  count, 
  icon, 
  bgColor, 
  textColor, 
  borderColor,
  link,
  isActionRequired = false,
  description,
}) {
  const CardWrapper = link ? Link : 'div';
  const cardProps = link ? { to: link } : {};

  return (
    <CardWrapper
      {...cardProps}
      className={`
        relative p-3 sm:p-4 rounded-lg border-l-4 ${borderColor} ${bgColor}
        ${link ? 'hover:shadow-md transition-shadow cursor-pointer' : ''}
        ${isActionRequired ? 'ring-2 ring-amber-400 ring-offset-1' : ''}
      `}
    >
      {isActionRequired && (
        <span className="absolute -top-2 -right-2 flex h-5 w-5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-5 w-5 bg-amber-500 items-center justify-center text-white text-xs font-bold">!</span>
        </span>
      )}
      
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-xs sm:text-sm text-gray-500 mb-1">{label}</p>
          <p className="text-xl sm:text-2xl font-bold {textColor}">{count}</p>
          {description && (
            <p className="text-xs text-gray-500 mt-1">{description}</p>
          )}
        </div>
        {icon && (
          <div className={`${textColor} opacity-60`}>
            {icon}
          </div>
        )}
      </div>
    </CardWrapper>
  );
}

WorkflowStatusCard.propTypes = {
  label: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  icon: PropTypes.node,
  bgColor: PropTypes.string,
  textColor: PropTypes.string,
  borderColor: PropTypes.string,
  link: PropTypes.string,
  isActionRequired: PropTypes.bool,
  description: PropTypes.string,
};

/**
 * Legal Workflow Stats Component
 * Displays announcement counts by workflow status
 */
export default function LegalWorkflowStats({ 
  drafts = 0,
  pending = 0,
  published = 0,
  awaitingPayment = 0,
  isLoading = false,
  lastUpdated = null,
}) {
  // Debug log
  console.log('LegalWorkflowStats props:', { drafts, pending, published, awaitingPayment });
  
  // Calculate totals and percentages - include awaitingPayment in total
  const total = drafts + pending + published + awaitingPayment;
  const completionRate = total > 0 ? Math.round((published / total) * 100) : 0;

  if (isLoading) {
    return (
      <div className="mt-6 px-4 sm:px-6 lg:px-9">
        <div className="flex items-center justify-between mb-4">
          <div className="h-6 w-48 bg-gray-200 rounded animate-pulse" />
          <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-24 bg-gray-100 rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6 px-4 sm:px-6 lg:px-9">
      {/* Section Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2">
        <div>
          <h4 className="text-sm sm:text-base md:text-lg font-semibold text-primary">
            Mes annonces légales
          </h4>
          <p className="text-sm text-gray-500">
            Vue d'ensemble de vos dossiers en cours
          </p>
        </div>
        
        {/* Completion indicator */}
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className="text-xs text-gray-500">Taux de publication</p>
            <p className="text-lg font-bold text-primary">{completionRate}%</p>
          </div>
          <div className="w-16 h-16 relative">
            <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 36 36">
              <path
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                fill="none"
                stroke="#e5e7eb"
                strokeWidth="3"
              />
              <path
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                fill="none"
                stroke="#1cbe93"
                strokeWidth="3"
                strokeDasharray={`${completionRate}, 100`}
                strokeLinecap="round"
              />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-gray-600">
              {total}
            </span>
          </div>
        </div>
      </div>

      {/* Workflow Status Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-5">
        {/* Drafts */}
        <WorkflowStatusCard
          label="Brouillons"
          count={drafts}
          bgColor="bg-gray-50"
          textColor="text-gray-700"
          borderColor="border-gray-400"
          link="/prouser/mes-dossiers?status=draft"
          description="À soumettre"
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          }
        />

        {/* Awaiting Payment */}
        <WorkflowStatusCard
          label="Devis en attente"
          count={awaitingPayment}
          bgColor="bg-amber-50"
          textColor="text-amber-700"
          borderColor="border-amber-400"
          link="/prouser/annoncesenattente"
          isActionRequired={awaitingPayment > 0}
          description="En attente de validation client"
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          }
        />

        {/* Pending/Processing */}
        <WorkflowStatusCard
          label="En traitement"
          count={pending}
          bgColor="bg-blue-50"
          textColor="text-blue-700"
          borderColor="border-blue-400"
          link="/prouser/annoncesenattente"
          description="En cours de traitement"
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />

        {/* Published */}
        <WorkflowStatusCard
          label="Publiées"
          count={published}
          bgColor="bg-green-50"
          textColor="text-green-700"
          borderColor="border-green-400"
          link="/prouser/annoncespublies"
          description="Finalisées"
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
      </div>

      {/* Quick Actions Bar */}
      {(drafts > 0 || awaitingPayment > 0) && (
        <div className="mt-4 p-3 sm:p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p className="text-xs sm:text-sm font-medium text-amber-800">
                {awaitingPayment > 0 
                  ? `${awaitingPayment} devis en attente de validation`
                  : `${drafts} brouillon(s) non soumis`
                }
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {drafts > 0 && (
                <Link
                  to="/prouser/mes-dossiers?status=draft"
                  className="px-2 sm:px-3 py-1.5 text-xs font-medium text-amber-700 bg-white border border-amber-300 rounded hover:bg-amber-50 transition-colors"
                >
                  Voir les devis en attente
                  <svg className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              )}
              {awaitingPayment > 0 && (
                <Link
                  to="/prouser/annoncesenattente"
                  className="px-2 sm:px-3 py-1.5 text-xs font-medium text-amber-700 bg-white border border-amber-300 rounded hover:bg-amber-50 transition-colors"
                >
                  Voir les devis en attente
                  <svg className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

LegalWorkflowStats.propTypes = {
  drafts: PropTypes.number,
  pending: PropTypes.number,
  published: PropTypes.number,
  awaitingPayment: PropTypes.number,
  isLoading: PropTypes.bool,
  lastUpdated: PropTypes.instanceOf(Date),
};
